import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import { useState } from "react";
import { useNavigate } from "react-router";
import SearchField from "../../interface/shared/searchfield";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { ProviderProducts, useProducts } from "../../api/products";
import { useSuppliers } from "../../api/suppliers";
import { Supplier } from "../../shared/types";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { FadeIn } from "../../interface/shared/animations";
import { WeightDisplay } from "../../interface/shared/weightdisplay";

export default function Products() {
    return (
        <DashboardLayout>
            <ProviderProducts>
                <ProductsContent />
            </ProviderProducts>
        </DashboardLayout>
    );
}

function ProductsContent() {
    const {
        products,
        errors,
        loading,
        fetchProducts,
        page,
        perPage,
        total,
        setPage,
        setPerPage,
        searchTerm,
        setSearchTerm,
        includeInactive,
        setIncludeInactive,
        setSupplierId,
        sortField,
        sortOrder,
        setSort,
    } = useProducts();
    const {
        suppliers,
        loading: suppliersLoading,
        setSearchTerm: setSupplierSearchTerm,
        ensureLoaded: ensureSuppliersLoaded,
    } = useSuppliers();
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
    const navigate = useNavigate();

    const supplierOptions =
        selectedSupplier && !suppliers.some((s) => s.id === selectedSupplier.id)
            ? [selectedSupplier, ...suppliers]
            : suppliers;

    const handleSupplierChange = (_: unknown, value: Supplier | null) => {
        setSelectedSupplier(value);
        setSupplierId(value?.id ?? null);
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Products
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All products in the warehouse.
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    <Autocomplete
                        options={supplierOptions}
                        loading={suppliersLoading}
                        value={selectedSupplier}
                        onChange={handleSupplierChange}
                        onOpen={ensureSuppliersLoaded}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear") {
                                setSupplierSearchTerm(value);
                            }
                        }}
                        getOptionLabel={(option) =>
                            option.code ? `${option.name} (${option.code})` : option.name
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(options) => options}
                        sx={{
                            minWidth: { xs: "100%", sm: 260 },
                            "& .MuiIconButton-root": {
                                border: "none",
                                background: "none",
                                backgroundColor: "transparent",
                                "&:hover": {
                                    background: "none",
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Filter by supplier"
                                hiddenLabel
                                label=""
                                size="small"
                            />
                        )}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                                size="small"
                            />
                        }
                        label="Show inactive"
                        sx={{ mr: 0, whiteSpace: "nowrap" }}
                    />
                    <SearchField
                        placeholder="Search products by name, barcode, code or main code"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />
                </Stack>
            </Stack>
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchProducts}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Main Code</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Size</TableCell>
                                    <TableCell>Pack</TableCell>
                                    <TableCell>Weight</TableCell>
                                    <TableCell align="center">Active</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="center">
                                            <Box alignItems="center" justifyContent="center" display="flex"><Skeleton variant="circular" width={20} height={20} /></Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={sortField === "code" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "code"}
                                                direction={sortField === "code" ? sortOrder : "asc"}
                                                onClick={() => setSort("code")}
                                            >
                                                Code
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Main Code</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell sortDirection={sortField === "size" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "size"}
                                                direction={sortField === "size" ? sortOrder : "asc"}
                                                onClick={() => setSort("size")}
                                            >
                                                Size
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "pack" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "pack"}
                                                direction={sortField === "pack" ? sortOrder : "asc"}
                                                onClick={() => setSort("pack")}
                                            >
                                                Pack
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "weight" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "weight"}
                                                direction={sortField === "weight" ? sortOrder : "asc"}
                                                onClick={() => setSort("weight")}
                                            >
                                                Weight
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sortDirection={sortField === "active" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "active"}
                                                direction={sortField === "active" ? sortOrder : "asc"}
                                                onClick={() => setSort("active")}
                                            >
                                                Active
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No products found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : products.map((product) => (
                                        <TableRow key={`${product.name}`}>
                                            <TableCell>{product.code}</TableCell>
                                            <TableCell>{product.main_code}</TableCell>
                                            <TableCell>{product.name}</TableCell>
                                            <TableCell>{product.size}</TableCell>
                                            <TableCell>{product.pack}</TableCell>
                                            <TableCell><WeightDisplay value={product.weight} /></TableCell>
                                            <TableCell align="center">
                                                {product.active ? (
                                                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelRoundedIcon color="error" fontSize="small" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/products/${product.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}